import { Injectable } from '@angular/core';
import { Observable, of, ReplaySubject, Subject } from 'rxjs';
import { ServerResponse } from 'src/app/models/ServerResponse';
import { finalize, map } from 'rxjs/operators';
import { AuthRequestsService } from './auth-requests.service';

// Modelos
import { TokenModel } from '../models/TokenModel';
import { UserModel } from '../models/UserModel';
import { UserType } from '../models/UserType';
import { SignUpAllFieldsBuyer } from '../classes/SignUpAllFieldsBuyer';
import { SignUpAllFieldsSeller } from '../classes/SignUpAllFieldsSeller';
import { EmailConfirmationFields } from '../classes/EmailConfirmationFields';

// Google
import { AngularFireAuth } from '@angular/fire/compat/auth';
import * as auth from 'firebase/auth';
import { GoogleLoginData } from '../classes/GoogleLoginData';
import { PasswordRecoveryFields } from '../classes/PasswordRecoveryFields';

@Injectable({
	providedIn: 'root'
})
export class AuthService {

	private ls_key_token : string;
	private ls_key_user : string;
	private ls_key_refreshToken : string;
	private sUser : ReplaySubject<UserType>;
	oUser : Observable<UserType>;
	token : TokenModel | undefined;
	refresh_token : string | undefined;
	private queryMeSemaphore = true;

	constructor(private ar:AuthRequestsService,
				private firebaseAuth:AngularFireAuth) {
		this.ls_key_token = "token";
		this.ls_key_user = "user";
		this.ls_key_refreshToken = "refreshToken"
		this.sUser = new ReplaySubject<UserType>(1);
		this.oUser = this.sUser.asObservable();
		this.isLoggedIn();
	}

	//#region Sesión
	login(email:string, password:string) : Observable<ServerResponse<UserModel>> {
		return this.ar.login(email, password);
	}
	isLoggedIn() : boolean {
		let lst = localStorage.getItem(this.ls_key_token);
		let lsu = localStorage.getItem(this.ls_key_user);
		if (!lst || !lsu) {
			this.removeLocalSession();
			return false;
		}

		this.token = localStorage.getItem('token') ? JSON.parse(localStorage.getItem(this.ls_key_token)!) : undefined;
		this.sUser.next(<UserType>JSON.parse(localStorage.getItem(this.ls_key_user)!));
		this.refresh_token = localStorage.getItem(this.ls_key_refreshToken) ?? undefined;

		this.me().subscribe();
		return true;
	}
	me() : Observable<void> {
		if (this.queryMeSemaphore) {
			this.queryMeSemaphore = false;

			this.token = <TokenModel>JSON.parse(localStorage.getItem(this.ls_key_token)!);
			return this.ar.me(this.token).pipe(
				map((sr: ServerResponse<UserModel>) => {
					if (sr.response)
						this.updateLocalUser(sr.response);
					else
						this.removeLocalSession();
				}),
				finalize(() => {
					this.queryMeSemaphore = true;
				})
			);

		} else {
			return of();
		}
	}
	refreshToken(): Observable<ServerResponse<UserModel>> {
		return this.ar.refreshToken(this.token!);
	}
	//#endregion

	//#region Registro
	isThisEmailTaken(email: string): Observable<boolean> {
		email = email.toLowerCase();
		return this.ar.isThisEmailTaken(email).pipe(
			map((sr: ServerResponse<boolean>) => {
				return sr.response!;
			})
		);
	}
	isThisCuitTaken(cuit_cuil:string) : Observable<boolean> {
		return this.ar.isThisCuitTaken(cuit_cuil).pipe(
			map((sr:ServerResponse<boolean>)=> {
				return sr.response!;
			})
		);
	}
	signUp(fields:SignUpAllFieldsBuyer|SignUpAllFieldsSeller) : Observable<ServerResponse<true>> {
		return this.ar.signUp(fields);
	}
	emailConfirmation(emailConfirmationFields:EmailConfirmationFields) : Observable<boolean> {
		return this.ar.emailConfirmation(emailConfirmationFields).pipe(
			map((sr:ServerResponse<boolean>)=> {
				return sr.response!;
			})
		);
	}
	//#endregion

	//#region Google
	async googleSignInPopUp() : Promise<GoogleLoginData> {
		return this.firebaseAuth.signInWithPopup(new auth.GoogleAuthProvider())
			.then((result) => {
				return result.user!.getIdToken().then((token)=> {
					let gld = new GoogleLoginData(token, result.user!.email!);
					return Promise.resolve(gld);
				}).catch(()=> {
					return Promise.reject();
				});
			})
			.catch(() => {
				return Promise.reject();
			});
	}
	loginWithGoogleToken(googleToken:string) : Observable<void> {
		return this.ar.loginWithGoogle(googleToken).pipe(
			map((sr:ServerResponse<UserModel>)=> {
				this.updateLocalUser(sr.response!);
			}),
		);
	}
	//#endregion

	logout() : void {
		this.removeLocalSession();
	}

	//#region Password
	forgotPassword(email:string) : Observable<ServerResponse<boolean>> {
		return this.ar.forgotPassword(email);
	}
	passwordRecovery(fields:PasswordRecoveryFields) : Observable<boolean> {
		return this.ar.passwordRecovery(fields).pipe(
			map((sr:ServerResponse<boolean>)=> {
				return sr.response!
			}),
		);
	}
	//#endregion

	updateLocalUser(user:UserModel) : void {
		if (!user) {
			throw new Error("Undefined user in updateLocalUser");
		}

		if (user.access_token) {
			this.token = new TokenModel(user.access_token, user.token_type!, user.expires!);
			this.saveToken(this.token);

			delete user.access_token;
			delete user.token_type;
			delete user.expires;
		}

		if (user.refresh_token) {
			this.refresh_token = user.refresh_token;
			localStorage.setItem(this.ls_key_refreshToken, user.refresh_token);
			delete user.refresh_token;
		}

		localStorage.setItem(this.ls_key_user, JSON.stringify(user));
		this.sUser.next(user);
	}

	saveToken(token: TokenModel) {
		localStorage.setItem(this.ls_key_token, JSON.stringify(token));
		this.token = token;
	}

	getToken() {
		if (this.token) {
		return this.token;
		}
		return JSON.parse(localStorage.getItem(this.ls_key_token) ?? '{}');
	}

	private removeLocalSession() {
		localStorage.removeItem(this.ls_key_token);
		localStorage.removeItem(this.ls_key_user);
		localStorage.removeItem(this.ls_key_refreshToken);
		this.sUser.next(undefined);
		this.token = undefined;
	}
}
